import axios from 'axios'
import { API_BASE_URL, X_API_KEY } from './api'
import * as Sentry from '@sentry/react'

const fetchResourceByUserId = async (
  userId: string,
  token: string,
  resourceType: string,
) => {
  const url = `${API_BASE_URL}/user/${userId}/${resourceType}`

  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-api-key': X_API_KEY,
      },
    })

    console.log(`response (${resourceType})`, response.data)

    return response.data[resourceType]
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if ([404, 500].includes(error.response?.status!)) {
        return null
      }

      Sentry.captureException(error, {
        extra: { action: `fetch${resourceType}ByUserId`, userId },
        tags: { type: 'api_error' },
      })

      return null
    }
    throw error
  }
}

const deleteResource = async (
  resourceId: string | number,
  token: string,
  resourceType: string,
) => {
  const url = `${API_BASE_URL}/user/${resourceId}/${resourceType}`

  try {
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'x-api-key': X_API_KEY,
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to delete ${resourceType} with ID ${resourceId}`)
    }

    console.log(`${resourceType} with ID ${resourceId} deleted successfully`)
  } catch (error) {
    console.error(
      `Error deleting ${resourceType} with ID ${resourceId}:`,
      error,
    )
    throw error
  }
}

// Fetch functions
export const fetchAnnounceByUserId = (userId: string, token: string) =>
  fetchResourceByUserId(userId, token, 'announce')

export const fetchEventByUserId = (userId: string, token: string) =>
  fetchResourceByUserId(userId, token, 'event')

export const fetchWorkByUserId = (userId: string, token: string) =>
  fetchResourceByUserId(userId, token, 'work')

export const fetchEstablishmentByUserId = (userId: string, token: string) =>
  fetchResourceByUserId(userId, token, 'establishment')

export const fetchLibertinByUserId = (userId: string, token: string) =>
  fetchResourceByUserId(userId, token, 'libertin')

// Delete functions
export const deleteAnnounce = (announceId: string, token: string) =>
  deleteResource(announceId, token, 'announce')

export const deleteEvent = (eventId: string, token: string) =>
  deleteResource(eventId, token, 'event')

export const deleteWork = (workId: string, token: string) =>
  deleteResource(workId, token, 'work')

export const deleteEstablishment = (establishmentId: string, token: string) =>
  deleteResource(establishmentId, token, 'establishment')

export const deleteLibertin = (libertinId: string, token: string) =>
  deleteResource(libertinId, token, 'libertin')

// Composed functions (fetch then delete by userId)
export const deleteMyAnnounceByUserId = async (
  userId: string,
  token: string,
) => {
  const announce = await fetchAnnounceByUserId(userId, token)
  if (announce?.id) {
    await deleteAnnounce(announce.id, token)
  }
}

export const deleteMyEventByUserId = async (userId: string, token: string) => {
  const event = await fetchEventByUserId(userId, token)
  if (event?.id) {
    await deleteEvent(event.id, token)
  }
}

export const deleteMyWorkByUserId = async (userId: string, token: string) => {
  const work = await fetchWorkByUserId(userId, token)
  if (work?.id) {
    await deleteWork(work.id, token)
  }
}

export const deleteMyEstablishmentByUserId = async (
  userId: string,
  token: string,
) => {
  const establishment = await fetchEstablishmentByUserId(userId, token)
  if (establishment?.id) {
    await deleteEstablishment(establishment.id, token)
  }
}

export const deleteMyLibertinByUserId = async (
  userId: string,
  token: string,
) => {
  const libertin = await fetchLibertinByUserId(userId, token)
  if (libertin?.id) {
    await deleteLibertin(libertin.id, token)
  }
}
